/**
 * The start sheet of Create → "New scene" (spec 029, spec 002): a wrapper of
 * the shared `StartSheet` (D-1) with "✨ Describe your scene" / "Start with
 * AI" on top and a "Blank scene" card below an "or" divider. No Length row
 * (D-2). Opening it creates nothing.
 *
 * - Start with AI creates the scene in the current folder, named after the
 *   brief's first line (`nameFromBrief`), stores the fixed brief text as its
 *   pending scene brief (`setPendingSceneBrief`, D-4) and opens it in the
 *   scene studio; the scene editor sends it as the scene's first turn.
 * - Blank scene creates "New Scene" (unique among the folder's scenes, 002)
 *   and opens it in the scene studio (D-8).
 * - With the assistant unavailable, only "Blank scene" works.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useAssistantStore } from '../../stores/assistantStore';
import { uniqueName } from './organiser';
import StartSheet, { nameFromBrief, type StartSheetValues } from './StartSheet';

export const BLANK_SCENE_NAME = 'New Scene';

export const SCENE_START_TEXT = {
  title: 'New scene',
  heading: '✨ Describe your scene',
  placeholder: 'A customer calls about a late delivery. The agent apologises, checks the order and offers a fix…',
  audiencePlaceholder: 'Who is it for? For example, first-week support staff',
  blankTitle: 'Blank scene',
  blankHint: 'Start from an empty stage',
} as const;

export interface SceneBrief { brief: string; objective?: string; audience?: string }

/**
 * The fixed first message of a scene started with AI, one field per line, empty fields left out (D-3).
 * Casting and picture notes are the scene assistant's own rules (022 D-11, 030), not part of the brief.
 */
export function sceneBriefMessage({ brief, objective, audience }: SceneBrief): string {
  const lines = ['Build this scene from scratch.', `Brief: ${brief.trim()}`];
  if (objective?.trim()) lines.push(`Learning objective: ${objective.trim()}`);
  if (audience?.trim()) lines.push(`Audience: ${audience.trim()}`);
  return lines.join('\n');
}

export interface SceneStartSheetProps {
  show: boolean;
  onHide: () => void;
  /** The folder the Create menu was opened in (null: Home). */
  folderId: number | null;
  /** The names of the scenes in that folder, for unique names. */
  siblingSceneNames: Array<string | null | undefined>;
  /** Passed as router state, like opening any card (002). */
  from?: string;
}

const errorOf = (res: { success: boolean; error?: string } | null | undefined) =>
  (res && !res.success && res.error) || 'Create failed';

export const SceneStartSheet: React.FC<SceneStartSheetProps> = ({ show, onHide, folderId, siblingSceneNames, from }) => {
  const navigate = useNavigate();

  const open = (id: number) => navigate(`/scene/${id}?view=studio`, from ? { state: { from } } : undefined);

  /** Creates the scene; a failure throws the sheet's error text. */
  const create = async (name: string): Promise<number> => {
    let res: Awaited<ReturnType<typeof apiClient.createScene>>;
    try {
      res = await apiClient.createScene({ name, folderId });
    } catch (e) {
      throw new Error(`Could not create a scene: ${e instanceof Error ? e.message : 'Create failed'}`);
    }
    if (!res.success) throw new Error(`Could not create a scene: ${errorOf(res)}`);
    return res.data.id;
  };

  const startWithAI = async ({ brief, objective, audience }: StartSheetValues) => {
    const id = await create(uniqueName(nameFromBrief(brief), siblingSceneNames));
    useAssistantStore.getState().setPendingSceneBrief(id, sceneBriefMessage({ brief, objective, audience }));
    onHide();
    open(id);
  };

  const blankScene = async () => {
    const id = await create(uniqueName(BLANK_SCENE_NAME, siblingSceneNames));
    onHide();
    open(id);
  };

  return (
    <StartSheet
      show={show}
      onHide={onHide}
      testIdPrefix="scene-start"
      title={SCENE_START_TEXT.title}
      heading={SCENE_START_TEXT.heading}
      placeholder={SCENE_START_TEXT.placeholder}
      audiencePlaceholder={SCENE_START_TEXT.audiencePlaceholder}
      blankTitle={SCENE_START_TEXT.blankTitle}
      blankHint={SCENE_START_TEXT.blankHint}
      onStart={startWithAI}
      onBlank={blankScene}
    />
  );
};

export default SceneStartSheet;
